/**
 * The path prefix this copy of the application is served under.
 *
 * Every release is published twice from two builds: at the root, which always points at the
 * newest, and at `/vX.Y.Z/`, frozen, so a delivery under review is not replaced in silence by
 * the next one. The frozen one is built with `--base=/vX.Y.Z/`, so Vite already knows the
 * prefix and puts it in `BASE_URL`.
 *
 * Read from there rather than parsed out of `location.pathname`. A regular expression over the
 * path is a guess about which segments are a version and which are a route, and it is wrong in
 * a way that is hard to see: a future route called `/vendas` would be read as a version prefix
 * and every page under it would resolve one segment short. `BASE_URL` is what the bundler was
 * told, so it cannot disagree with where the assets actually are.
 *
 * Without this the frozen copies answered 404 to everything. CloudFront resolved a deep link
 * to the right `index.html`, the right assets loaded, and then the router compared
 * `/v0.4.0/catalogo` against a route table that only knows `/catalogo` and found nothing. The
 * checks that passed used `curl`, which fetches a document and never runs a router.
 */
export function releaseBasename(base: string = import.meta.env.BASE_URL): string {
  // Vite always leaves a trailing slash; React Router wants the prefix without one, and the
  // root has to stay exactly '/'.
  const trimmed = base.replace(/\/+$/u, '');
  return trimmed === '' ? '/' : trimmed;
}

/** True when this copy is one of the frozen per-release publications rather than the root. */
export function isFrozenRelease(base: string = import.meta.env.BASE_URL): boolean {
  return releaseBasename(base) !== '/';
}
